import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { KTX2Loader } from "three/examples/jsm/loaders/KTX2Loader.js";
import { MeshoptDecoder } from "three/examples/jsm/libs/meshopt_decoder.module.js";
import { RoundedBoxGeometry } from "three/addons/geometries/RoundedBoxGeometry.js";
import heroVehicleUrl from "../assets/vehicles/suntrace_car_concept_lod.glb?url";
import type { SuntraceQuality } from "./types";

export const SUNTRACE_HERO_VEHICLE_CONTRACT = {
  maximumTriangles: 90_000,
  maximumMeshes: 72,
  width: { minimum: 2.4, maximum: 2.7 },
  height: { minimum: 1, maximum: 1.3 },
  length: { minimum: 4.1, maximum: 4.6 },
  wheelbase: { minimum: 2.65, maximum: 2.95 },
} as const;

const WHEEL_NAMES = [
  "WheelFrontL",
  "WheelFrontR",
  "WheelRearL",
  "WheelRearR",
] as const;

const WHEEL_RIM_NAMES = [
  "WheelFrontLRim",
  "WheelFrontRRim",
  "WheelRearLRim",
  "WheelRearRRim",
] as const;

const REQUIRED_MATERIAL_NAMES = [
  "SUNTRACE Navy",
  "SUNTRACE Ivory",
  "SUNTRACE Vermilion",
  "SUNTRACE Brake Vermilion",
  "Tireside",
  "Tiretread",
  "Rim1",
  "Rim2",
] as const;

const LOW_QUALITY_HIDDEN_NODES = [
  "InteriorCage",
  "InteriorDashSides",
  "InteriorMid",
  "InteriorPillar",
  "InteriorSeatsFrame1",
  "InteriorSeatsFrame2",
] as const;

type VehicleMesh = THREE.Mesh<
  THREE.BufferGeometry,
  THREE.Material | THREE.Material[]
>;

export type SuntraceAuthoredWheelRig = {
  name: (typeof WHEEL_NAMES)[number];
  steeringPivot: THREE.Group;
  spinningGroup: THREE.Group;
};

export type SuntraceAuthoredVehicleAsset = {
  root: THREE.Group;
  wheels: readonly SuntraceAuthoredWheelRig[];
  wheelRadius: number;
  triangleCount: number;
  meshCount: number;
  setWheelPose: (frontSteer: number, wheelAngle: number) => void;
  setEnergy: (energy: number, impact: number) => void;
  setQuality: (quality: SuntraceQuality) => void;
  dispose: () => void;
};

export type SuntraceHeroVehicleLoadDependencies = {
  loadScene: () => Promise<THREE.Group>;
  abort?: () => void;
  dispose?: () => void;
};

type VehicleResources = {
  geometries: Set<THREE.BufferGeometry>;
  materials: Set<THREE.Material>;
  textures: Set<THREE.Texture>;
};

type VehicleContract = {
  wheels: readonly THREE.Object3D[];
  materials: Map<string, THREE.Material>;
  bounds: THREE.Box3;
  wheelRadius: number;
  triangleCount: number;
  meshCount: number;
  axleCenterZ: number;
};

const isVehicleMesh = (object: THREE.Object3D): object is VehicleMesh =>
  object instanceof THREE.Mesh;

const getMaterials = (mesh: VehicleMesh): THREE.Material[] =>
  Array.isArray(mesh.material) ? mesh.material : [mesh.material];

const getMaterialValues = (material: THREE.Material): unknown[] =>
  Object.values(material as unknown as Record<string, unknown>);

const isTexture = (value: unknown): value is THREE.Texture =>
  value instanceof THREE.Texture;

const collectVehicleResources = (root: THREE.Object3D): VehicleResources => {
  const geometries = new Set<THREE.BufferGeometry>();
  const materials = new Set<THREE.Material>();
  const textures = new Set<THREE.Texture>();
  root.traverse((object) => {
    if (!isVehicleMesh(object)) {
      return;
    }
    geometries.add(object.geometry);
    for (const material of getMaterials(object)) {
      materials.add(material);
      for (const value of getMaterialValues(material)) {
        if (isTexture(value)) {
          textures.add(value);
        }
      }
    }
  });
  return { geometries, materials, textures };
};

const disposeVehicleRoot = (root: THREE.Object3D): void => {
  const resources = collectVehicleResources(root);
  for (const texture of resources.textures) {
    texture.dispose();
  }
  for (const material of resources.materials) {
    material.dispose();
  }
  for (const geometry of resources.geometries) {
    geometry.dispose();
  }
  root.removeFromParent();
  root.clear();
};

const getTriangleCount = (root: THREE.Object3D): number => {
  let triangleCount = 0;
  root.traverse((object) => {
    if (!isVehicleMesh(object)) {
      return;
    }
    const positionCount = object.geometry.getAttribute("position").count;
    triangleCount += Math.floor((object.geometry.index?.count ?? positionCount) / 3);
  });
  return triangleCount;
};

const getMeshCount = (root: THREE.Object3D): number => {
  let meshCount = 0;
  root.traverse((object) => {
    if (isVehicleMesh(object)) {
      meshCount += 1;
    }
  });
  return meshCount;
};

const stripLowValueMechanicalDetail = (root: THREE.Object3D): void => {
  const underside = root.getObjectByName("BodyUnderside");
  if (underside && isVehicleMesh(underside)) {
    underside.geometry.setIndex([]);
    underside.geometry.computeBoundingBox();
    underside.geometry.computeBoundingSphere();
  }
  root.traverse((object) => {
    if (object.name.endsWith("BrakeDisc")) {
      object.visible = false;
    }
  });
};

const getNamedMaterials = (root: THREE.Object3D): Map<string, THREE.Material> => {
  const materials = new Map<string, THREE.Material>();
  root.traverse((object) => {
    if (!isVehicleMesh(object)) {
      return;
    }
    for (const material of getMaterials(object)) {
      if (material.name !== "") {
        materials.set(material.name, material);
      }
    }
  });
  return materials;
};

const getRequiredNode = (root: THREE.Object3D, name: string): THREE.Object3D => {
  const node = root.getObjectByName(name);
  if (node === undefined) {
    throw new Error(`Authored hero vehicle is missing ${name}`);
  }
  return node;
};

const validateRange = (
  label: string,
  value: number,
  range: { minimum: number; maximum: number },
): void => {
  if (!Number.isFinite(value) || value < range.minimum || value > range.maximum) {
    throw new Error(`Authored hero vehicle has an invalid ${label}`);
  }
};

export const validateSuntraceHeroVehicleScene = (
  scene: THREE.Group,
): VehicleContract => {
  scene.updateMatrixWorld(true);
  const wheels = WHEEL_NAMES.map((name) => getRequiredNode(scene, name));
  for (const name of WHEEL_RIM_NAMES) {
    getRequiredNode(scene, name);
  }
  if (wheels.some((wheel) => wheel.parent === null)) {
    throw new Error("Authored hero vehicle contains an orphaned wheel");
  }
  const wheelPositions = wheels.map((wheel) =>
    wheel.getWorldPosition(new THREE.Vector3()),
  );
  const frontZ = (wheelPositions[0]?.z ?? 0) + (wheelPositions[1]?.z ?? 0);
  const rearZ = (wheelPositions[2]?.z ?? 0) + (wheelPositions[3]?.z ?? 0);
  const wheelbase = (frontZ - rearZ) * 0.5;
  validateRange("wheelbase", wheelbase, SUNTRACE_HERO_VEHICLE_CONTRACT.wheelbase);
  if ((wheelPositions[0]?.x ?? 0) <= (wheelPositions[1]?.x ?? 0)) {
    throw new Error("Authored hero vehicle left and right wheels are inverted");
  }
  const bounds = new THREE.Box3().setFromObject(scene, true);
  const size = bounds.getSize(new THREE.Vector3());
  if (bounds.isEmpty() || !size.toArray().every(Number.isFinite)) {
    throw new Error("Authored hero vehicle has invalid bounds");
  }
  validateRange("width", size.x, SUNTRACE_HERO_VEHICLE_CONTRACT.width);
  validateRange("height", size.y, SUNTRACE_HERO_VEHICLE_CONTRACT.height);
  validateRange("length", size.z, SUNTRACE_HERO_VEHICLE_CONTRACT.length);
  if (bounds.min.y < -0.02 || bounds.min.y > 0.02) {
    throw new Error("Authored hero vehicle is not grounded");
  }
  const triangleCount = getTriangleCount(scene);
  const meshCount = getMeshCount(scene);
  if (
    triangleCount <= 0 ||
    triangleCount > SUNTRACE_HERO_VEHICLE_CONTRACT.maximumTriangles
  ) {
    throw new Error("Authored hero vehicle exceeds its triangle budget");
  }
  if (meshCount <= 0 || meshCount > SUNTRACE_HERO_VEHICLE_CONTRACT.maximumMeshes) {
    throw new Error("Authored hero vehicle exceeds its mesh budget");
  }
  const materials = getNamedMaterials(scene);
  for (const name of REQUIRED_MATERIAL_NAMES) {
    if (!materials.has(name)) {
      throw new Error(`Authored hero vehicle is missing ${name}`);
    }
  }
  const frontWheelBounds = new THREE.Box3().setFromObject(wheels[0] ?? scene, true);
  const wheelSize = frontWheelBounds.getSize(new THREE.Vector3());
  const wheelRadius = Math.max(wheelSize.y, wheelSize.z) * 0.5;
  if (!Number.isFinite(wheelRadius) || wheelRadius < 0.34 || wheelRadius > 0.43) {
    throw new Error("Authored hero vehicle has an invalid wheel radius");
  }
  return {
    wheels,
    materials,
    bounds,
    wheelRadius,
    triangleCount,
    meshCount,
    axleCenterZ: (frontZ + rearZ) * 0.25,
  };
};

const configureMaterial = (
  material: THREE.Material,
  color: number,
  roughness: number,
  metalness: number,
  environmentIntensity: number,
): void => {
  if (
    !(material instanceof THREE.MeshStandardMaterial) &&
    !(material instanceof THREE.MeshPhysicalMaterial)
  ) {
    throw new Error(`Authored hero vehicle material ${material.name} is unsupported`);
  }
  material.color.setHex(color);
  material.roughness = roughness;
  material.metalness = metalness;
  material.envMapIntensity = environmentIntensity;
  material.needsUpdate = true;
};

const configureMaterials = (materials: Map<string, THREE.Material>): void => {
  const navy = materials.get("SUNTRACE Navy");
  const chalk = materials.get("SUNTRACE Ivory");
  const vermilion = materials.get("SUNTRACE Vermilion");
  const brake = materials.get("SUNTRACE Brake Vermilion");
  const tireSide = materials.get("Tireside");
  const tireTread = materials.get("Tiretread");
  const rimInner = materials.get("Rim1");
  const rimOuter = materials.get("Rim2");
  if (
    navy === undefined ||
    chalk === undefined ||
    vermilion === undefined ||
    brake === undefined ||
    tireSide === undefined ||
    tireTread === undefined ||
    rimInner === undefined ||
    rimOuter === undefined
  ) {
    throw new Error("Authored hero vehicle materials are incomplete");
  }
  configureMaterial(navy, 0x205881, 0.26, 0.52, 1.48);
  configureMaterial(chalk, 0xd8dad5, 0.34, 0.16, 1.2);
  configureMaterial(vermilion, 0xb9432f, 0.38, 0.1, 1.12);
  configureMaterial(brake, 0xc83e2d, 0.28, 0, 0.9);
  if (navy instanceof THREE.MeshPhysicalMaterial) {
    navy.clearcoat = 0.8;
    navy.clearcoatRoughness = 0.2;
    navy.sheen = 0.12;
    navy.sheenColor.setHex(0x4f7da0);
    navy.sheenRoughness = 0.54;
  }
  if (chalk instanceof THREE.MeshPhysicalMaterial) {
    chalk.clearcoat = 0.68;
    chalk.clearcoatRoughness = 0.25;
  }
  for (const material of materials.values()) {
    if (
      material instanceof THREE.MeshStandardMaterial ||
      material instanceof THREE.MeshPhysicalMaterial
    ) {
      material.envMapIntensity = Math.max(material.envMapIntensity, 0.82);
    }
  }
  configureMaterial(tireSide, 0x151a1e, 0.8, 0, 0.18);
  configureMaterial(tireTread, 0x0c1013, 0.88, 0, 0.12);
  configureMaterial(rimInner, 0x222d34, 0.36, 0.68, 0.54);
  configureMaterial(rimOuter, 0x40515b, 0.28, 0.78, 0.72);
  for (const rim of [rimInner, rimOuter]) {
    if (
      rim instanceof THREE.MeshStandardMaterial ||
      rim instanceof THREE.MeshPhysicalMaterial
    ) {
      rim.emissive.setHex(0x000000);
      rim.emissiveIntensity = 0;
    }
  }
};

const createWheelRig = (
  wheel: THREE.Object3D,
  name: (typeof WHEEL_NAMES)[number],
): SuntraceAuthoredWheelRig => {
  const parent = wheel.parent;
  if (parent === null) {
    throw new Error(`Authored hero vehicle wheel ${name} is orphaned`);
  }
  const steeringPivot = new THREE.Group();
  steeringPivot.name = `${name} steering pivot`;
  steeringPivot.position.copy(wheel.position);
  const spinningGroup = new THREE.Group();
  spinningGroup.name = `${name} spinning group`;
  const sourcePoseNeutralizer = new THREE.Group();
  sourcePoseNeutralizer.name = `${name} source pose neutralizer`;
  sourcePoseNeutralizer.quaternion.copy(wheel.quaternion).invert();
  sourcePoseNeutralizer.scale.set(
    wheel.scale.x === 0 ? 1 : 1 / wheel.scale.x,
    wheel.scale.y === 0 ? 1 : 1 / wheel.scale.y,
    wheel.scale.z === 0 ? 1 : 1 / wheel.scale.z,
  );
  wheel.removeFromParent();
  wheel.position.set(0, 0, 0);
  sourcePoseNeutralizer.add(wheel);
  spinningGroup.add(sourcePoseNeutralizer);
  steeringPivot.add(spinningGroup);
  parent.add(steeringPivot);
  return { name, steeringPivot, spinningGroup };
};

const glyphs: Readonly<Record<string, readonly string[]>> = {
  "0": ["111", "101", "101", "101", "111"],
  "1": ["010", "110", "010", "010", "111"],
  A: ["01110", "10001", "11111", "10001", "10001"],
  C: ["01111", "10000", "10000", "10000", "01111"],
  E: ["11111", "10000", "11110", "10000", "11111"],
  N: ["10001", "11001", "10101", "10011", "10001"],
  R: ["11110", "10001", "11110", "10100", "10010"],
  S: ["01111", "10000", "01110", "00001", "11110"],
  T: ["11111", "00100", "00100", "00100", "00100"],
  U: ["10001", "10001", "10001", "10001", "01110"],
};

const createPlateTexture = (): THREE.DataTexture => {
  const width = 512;
  const height = 128;
  const data = new Uint8Array(width * height * 4);
  const setPixel = (x: number, y: number, color: readonly [number, number, number]): void => {
    const offset = ((height - 1 - y) * width + x) * 4;
    data[offset] = color[0];
    data[offset + 1] = color[1];
    data[offset + 2] = color[2];
    data[offset + 3] = 255;
  };
  const background = [226, 228, 222] as const;
  const graphite = [25, 37, 45] as const;
  const vermilion = [190, 58, 40] as const;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const border = x < 8 || x >= width - 8 || y < 8 || y >= height - 8;
      const accent = y >= 12 && y < 20;
      setPixel(x, y, border ? graphite : accent ? vermilion : background);
    }
  }
  const label = "SUNTRACE 01";
  const scale = 7;
  const spacing = 7;
  const glyphWidths = Array.from(
    { length: label.length },
    (_, index) => glyphs[label[index] ?? " "]?.[0]?.length ?? 2,
  );
  const textWidth = glyphWidths.reduce(
    (total, glyphWidth, index) => total + glyphWidth * scale + (index === 0 ? 0 : spacing),
    0,
  );
  let cursorX = Math.floor((width - textWidth) * 0.5);
  const cursorY = 48;
  for (let characterIndex = 0; characterIndex < label.length; characterIndex += 1) {
    const character = label[characterIndex] ?? " ";
    const glyph = glyphs[character];
    if (glyph !== undefined) {
      for (let row = 0; row < glyph.length; row += 1) {
        const bits = glyph[row] ?? "";
        for (let column = 0; column < bits.length; column += 1) {
          if (bits[column] !== "1") {
            continue;
          }
          for (let pixelY = 0; pixelY < scale; pixelY += 1) {
            for (let pixelX = 0; pixelX < scale; pixelX += 1) {
              setPixel(
                cursorX + column * scale + pixelX,
                cursorY + row * scale + pixelY,
                graphite,
              );
            }
          }
        }
      }
    }
    cursorX += (glyphWidths[characterIndex] ?? 2) * scale + spacing;
  }
  const texture = new THREE.DataTexture(data, width, height, THREE.RGBAFormat);
  texture.name = "SUNTRACE 01 rear plate texture";
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.generateMipmaps = true;
  texture.needsUpdate = true;
  return texture;
};

const addRearPlate = (
  root: THREE.Group,
  rearZ: number,
  groundOffset: number,
): void => {
  const rearPanelMaterial = new THREE.MeshStandardMaterial({
    color: 0x466979,
    roughness: 0.38,
    metalness: 0.34,
    envMapIntensity: 1.02,
  });
  const rearPanel = new THREE.Mesh(
    new RoundedBoxGeometry(1.18, 0.055, 0.03, 3, 0.014),
    rearPanelMaterial,
  );
  rearPanel.name = "SUNTRACE rear center panel";
  rearPanel.position.set(0, 0.61 - groundOffset, rearZ - 0.018);
  rearPanel.castShadow = false;
  rearPanel.receiveShadow = true;
  const rearGlassSillMaterial = new THREE.MeshStandardMaterial({
    color: 0x101d24,
    roughness: 0.42,
    metalness: 0.38,
    envMapIntensity: 0.58,
  });
  const rearGlassSill = new THREE.Mesh(
    new RoundedBoxGeometry(1.04, 0.048, 0.022, 2, 0.008),
    rearGlassSillMaterial,
  );
  rearGlassSill.name = "SUNTRACE rear glass sill";
  rearGlassSill.position.set(0, 0.735 - groundOffset, rearZ - 0.028);
  rearGlassSill.castShadow = false;
  rearGlassSill.receiveShadow = true;
  const rearSeamMaterial = new THREE.MeshStandardMaterial({
    color: 0x1b2a32,
    roughness: 0.5,
    metalness: 0.28,
    envMapIntensity: 0.58,
  });
  const rearSeams = [-0.42, 0.42].map((x, index) => {
    const seam = new THREE.Mesh(
      new RoundedBoxGeometry(0.022, 0.1, 0.022, 2, 0.006),
      rearSeamMaterial,
    );
    seam.name = `SUNTRACE rear panel seam ${String(index + 1)}`;
    seam.position.set(x, 0.61 - groundOffset, rearZ - 0.039);
    seam.rotation.z = x < 0 ? -0.08 : 0.08;
    seam.castShadow = false;
    seam.receiveShadow = true;
    return seam;
  });
  const trim = new THREE.Mesh(
    new THREE.BoxGeometry(0.62, 0.16, 0.01),
    new THREE.MeshStandardMaterial({
      color: 0x1c2931,
      roughness: 0.52,
      metalness: 0.58,
      envMapIntensity: 0.9,
    }),
  );
  trim.name = "SUNTRACE 01 rear plate trim";
  trim.position.set(0, 0.405 - groundOffset, rearZ - 0.002);
  trim.rotation.y = Math.PI;
  trim.castShadow = false;
  trim.receiveShadow = true;
  const plate = new THREE.Mesh(
    new THREE.PlaneGeometry(0.56, 0.12),
    new THREE.MeshPhysicalMaterial({
      color: 0xffffff,
      map: createPlateTexture(),
      roughness: 0.42,
      metalness: 0,
      clearcoat: 0.36,
      clearcoatRoughness: 0.3,
      envMapIntensity: 0.76,
    }),
  );
  plate.name = "SUNTRACE 01 rear plate";
  plate.position.set(0, 0.405 - groundOffset, rearZ - 0.008);
  plate.rotation.y = Math.PI;
  plate.castShadow = false;
  plate.receiveShadow = true;
  const fasciaMaterial = new THREE.MeshStandardMaterial({
    color: 0x152733,
    roughness: 0.5,
    metalness: 0.34,
    envMapIntensity: 0.42,
  });
  const recessedFascia = new THREE.Mesh(
    new RoundedBoxGeometry(1.08, 0.17, 0.012, 1, 0.004),
    fasciaMaterial,
  );
  recessedFascia.name = "SUNTRACE recessed rear fascia";
  recessedFascia.position.set(0, 0.542 - groundOffset, rearZ - 0.025);
  recessedFascia.castShadow = false;
  recessedFascia.receiveShadow = true;
  const fasciaSeams = [-0.53, 0.53].map((x, index) => {
    const seam = new THREE.Mesh(
      new THREE.BoxGeometry(0.018, 0.135, 0.014),
      fasciaMaterial,
    );
    seam.name = `SUNTRACE rear fascia seam ${index === 0 ? "L" : "R"}`;
    seam.position.set(x, 0.542 - groundOffset, rearZ - 0.034);
    seam.castShadow = false;
    seam.receiveShadow = true;
    return seam;
  });
  const fasciaLowerEdge = new THREE.Mesh(
    new THREE.BoxGeometry(1.02, 0.018, 0.014),
    fasciaMaterial,
  );
  fasciaLowerEdge.name = "SUNTRACE rear fascia lower edge";
  fasciaLowerEdge.position.set(0, 0.45 - groundOffset, rearZ - 0.033);
  fasciaLowerEdge.castShadow = false;
  fasciaLowerEdge.receiveShadow = true;
  const taillightMaterial = new THREE.MeshStandardMaterial({
    color: 0x8b231a,
    roughness: 0.28,
    metalness: 0.04,
    emissive: 0x5e140f,
    emissiveIntensity: 0.72,
    envMapIntensity: 0.72,
  });
  const taillightBezelMaterial = new THREE.MeshStandardMaterial({
    color: 0x142129,
    roughness: 0.48,
    metalness: 0.34,
    envMapIntensity: 0.46,
  });
  const taillightBezels = [-0.46, 0.46].map((x, index) => {
    const bezel = new THREE.Mesh(
      new RoundedBoxGeometry(0.39, 0.09, 0.014, 1, 0.004),
      taillightBezelMaterial,
    );
    bezel.name = `SUNTRACE rear taillight bezel ${index === 0 ? "L" : "R"}`;
    bezel.position.set(x, 0.563 - groundOffset, rearZ - 0.034);
    bezel.castShadow = false;
    bezel.receiveShadow = true;
    return bezel;
  });
  const taillights = [-0.46, 0.46].map((x, index) => {
    const taillight = new THREE.Mesh(
      new THREE.BoxGeometry(0.33, 0.052, 0.018),
      taillightMaterial,
    );
    taillight.name = `SUNTRACE rear taillight ${index === 0 ? "L" : "R"}`;
    taillight.position.set(x, 0.563 - groundOffset, rearZ - 0.043);
    taillight.castShadow = false;
    taillight.receiveShadow = true;
    return taillight;
  });
  const diffuserMaterial = new THREE.MeshStandardMaterial({
    color: 0x536467,
    roughness: 0.44,
    metalness: 0.5,
    envMapIntensity: 0.88,
  });
  const diffuser = new THREE.Mesh(
    (() => {
      const shape = new THREE.Shape();
      shape.moveTo(-0.69, 0.045);
      shape.lineTo(0.69, 0.045);
      shape.lineTo(0.58, -0.045);
      shape.lineTo(-0.58, -0.045);
      shape.closePath();
      return new THREE.ExtrudeGeometry(shape, {
        depth: 0.14,
        bevelEnabled: true,
        bevelSegments: 2,
        bevelSize: 0.01,
        bevelThickness: 0.008,
        curveSegments: 2,
      });
    })(),
    diffuserMaterial,
  );
  diffuser.name = "SUNTRACE lower rear diffuser";
  diffuser.position.set(0, 0.276 - groundOffset, rearZ - 0.16);
  diffuser.rotation.x = -0.08;
  diffuser.castShadow = false;
  diffuser.receiveShadow = true;
  const diffuserLip = new THREE.Mesh(
    new RoundedBoxGeometry(1.3, 0.026, 0.035, 2, 0.008),
    new THREE.MeshStandardMaterial({
      color: 0x5a6668,
      roughness: 0.42,
      metalness: 0.66,
      envMapIntensity: 0.64,
    }),
  );
  diffuserLip.name = "SUNTRACE diffuser upper lip";
  diffuserLip.position.set(0, 0.314 - groundOffset, rearZ - 0.17);
  diffuserLip.rotation.x = -0.08;
  diffuserLip.castShadow = false;
  diffuserLip.receiveShadow = true;
  const diffuserFinMaterial = new THREE.MeshStandardMaterial({
    color: 0x1b2428,
    roughness: 0.58,
    metalness: 0.48,
    envMapIntensity: 0.42,
  });
  const diffuserFins = [-0.38, 0, 0.38].map((x, index) => {
    const fin = new THREE.Mesh(
      new RoundedBoxGeometry(0.024, 0.085, 0.11, 2, 0.007),
      diffuserFinMaterial,
    );
    fin.name = `SUNTRACE diffuser fin ${String(index + 1)}`;
    fin.position.set(x, 0.286 - groundOffset, rearZ - 0.14);
    fin.rotation.set(-0.08, 0, x === 0 ? 0 : Math.sign(x) * 0.12);
    fin.castShadow = false;
    fin.receiveShadow = true;
    return fin;
  });
  const rearVents = [-0.36, 0, 0.36].map((x, index) => {
    const vent = new THREE.Mesh(
      new THREE.BoxGeometry(0.12, 0.018, 0.014),
      diffuserFinMaterial,
    );
    vent.name = `SUNTRACE rear vent ${String(index + 1)}`;
    vent.position.set(x, 0.492 - groundOffset, rearZ - 0.044);
    vent.rotation.z = x === 0 ? 0 : Math.sign(x) * 0.06;
    vent.castShadow = false;
    vent.receiveShadow = true;
    return vent;
  });
  const exhaustHousingMaterial = new THREE.MeshStandardMaterial({
    color: 0x6a7676,
    roughness: 0.3,
    metalness: 0.82,
    envMapIntensity: 0.72,
  });
  const exhaustCoreMaterial = new THREE.MeshStandardMaterial({
    color: 0x151b1d,
    roughness: 0.38,
    metalness: 0.24,
    emissive: 0x321810,
    emissiveIntensity: 0.14,
    envMapIntensity: 0.26,
  });
  const exhaustDetails = [-0.46, 0.46].flatMap((x, index) => {
    const housing = new THREE.Mesh(
      new THREE.CylinderGeometry(0.095, 0.095, 0.045, 12, 1, true),
      exhaustHousingMaterial,
    );
    housing.name = `SUNTRACE exhaust housing ${String(index + 1)}`;
    housing.position.set(x, 0.268 - groundOffset, rearZ - 0.15);
    housing.rotation.x = Math.PI / 2;
    housing.castShadow = false;
    housing.receiveShadow = true;
    const core = new THREE.Mesh(
      new THREE.CylinderGeometry(0.062, 0.062, 0.05, 12),
      exhaustCoreMaterial,
    );
    core.name = `SUNTRACE exhaust core ${String(index + 1)}`;
    core.position.set(x, 0.268 - groundOffset, rearZ - 0.112);
    core.rotation.x = Math.PI / 2;
    core.castShadow = false;
    core.receiveShadow = true;
    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(0.086, 0.012, 6, 16),
      exhaustHousingMaterial,
    );
    ring.name = `SUNTRACE exhaust ring ${String(index + 1)}`;
    ring.position.set(x, 0.268 - groundOffset, rearZ - 0.178);
    ring.castShadow = false;
    ring.receiveShadow = true;
    const pipe = new THREE.Mesh(
      new THREE.CylinderGeometry(0.028, 0.035, 0.16, 8),
      diffuserFinMaterial,
    );
    pipe.name = `SUNTRACE exhaust pipe ${String(index + 1)}`;
    pipe.position.set(x, 0.268 - groundOffset, rearZ - 0.07);
    pipe.rotation.x = Math.PI / 2;
    pipe.castShadow = false;
    pipe.receiveShadow = true;
    return [housing, core, ring, pipe];
  });
  root.add(
    rearPanel,
    rearGlassSill,
    ...rearSeams,
    trim,
    plate,
    recessedFascia,
    ...fasciaSeams,
    fasciaLowerEdge,
    ...taillightBezels,
    ...taillights,
    ...rearVents,
    diffuser,
    diffuserLip,
    ...diffuserFins,
    ...exhaustDetails,
  );
};

export const createSuntraceAuthoredVehicleAsset = (
  scene: THREE.Group,
): SuntraceAuthoredVehicleAsset => {
  const contract = validateSuntraceHeroVehicleScene(scene);
  configureMaterials(contract.materials);
  stripLowValueMechanicalDetail(scene);
  scene.traverse((object) => {
    if (isVehicleMesh(object)) {
      object.castShadow = false;
      object.receiveShadow = true;
    }
  });
  const wheels = contract.wheels.map((wheel, index) =>
    createWheelRig(wheel, WHEEL_NAMES[index] ?? "WheelRearR"),
  );
  const root = new THREE.Group();
  root.name = "SUNTRACE authored hero";
  const center = contract.bounds.getCenter(new THREE.Vector3());
  scene.position.set(
    scene.position.x - center.x,
    scene.position.y - contract.bounds.min.y,
    scene.position.z - contract.axleCenterZ,
  );
  root.add(scene);
  addRearPlate(
    root,
    contract.bounds.min.z - contract.axleCenterZ - 0.012,
    contract.bounds.min.y,
  );
  const brakeMaterial = contract.materials.get("SUNTRACE Brake Vermilion");
  const headlightMaterial = contract.materials.get("Headlight");
  const lowQualityNodes = LOW_QUALITY_HIDDEN_NODES.map((name) =>
    scene.getObjectByName(name),
  ).filter((node): node is THREE.Object3D => node !== undefined);
  let disposed = false;
  return {
    root,
    wheels,
    wheelRadius: contract.wheelRadius,
    triangleCount: contract.triangleCount,
    meshCount: contract.meshCount,
    setWheelPose(frontSteer, wheelAngle) {
      for (let index = 0; index < wheels.length; index += 1) {
        const wheel = wheels[index];
        if (wheel === undefined) {
          continue;
        }
        wheel.steeringPivot.rotation.z = index < 2 ? frontSteer : 0;
        wheel.spinningGroup.rotation.x = wheelAngle;
      }
    },
    setEnergy(energy, impact) {
      const normalizedEnergy = THREE.MathUtils.clamp(energy, 0, 1);
      const normalizedImpact = THREE.MathUtils.clamp(impact, 0, 1);
      if (
        brakeMaterial instanceof THREE.MeshStandardMaterial ||
        brakeMaterial instanceof THREE.MeshPhysicalMaterial
      ) {
        brakeMaterial.emissiveIntensity = 2.15 + normalizedImpact * 1.1;
      }
      if (
        headlightMaterial instanceof THREE.MeshStandardMaterial ||
        headlightMaterial instanceof THREE.MeshPhysicalMaterial
      ) {
        headlightMaterial.emissiveIntensity = 3.25 + normalizedEnergy * 0.55;
      }
    },
    setQuality(quality) {
      for (const node of lowQualityNodes) {
        node.visible = quality !== "low";
      }
      scene.traverse((object) => {
        if (isVehicleMesh(object)) {
          object.castShadow = false;
          object.receiveShadow = quality !== "low";
        }
      });
    },
    dispose() {
      if (disposed) {
        return;
      }
      disposed = true;
      disposeVehicleRoot(root);
    },
  };
};

const createProductionLoader = (
  renderer: THREE.WebGLRenderer,
): SuntraceHeroVehicleLoadDependencies => {
  const loadingManager = new THREE.LoadingManager();
  const ktx2Loader = new KTX2Loader(loadingManager)
    .setTranscoderPath(`${import.meta.env.BASE_URL}basis/`)
    .detectSupport(renderer);
  const loader = new GLTFLoader(loadingManager)
    .setKTX2Loader(ktx2Loader)
    .setMeshoptDecoder(MeshoptDecoder);
  return {
    async loadScene() {
      const gltf = await loader.loadAsync(heroVehicleUrl);
      return gltf.scene;
    },
    abort() {
      loadingManager.abort();
    },
    dispose() {
      ktx2Loader.dispose();
    },
  };
};

const createAbortError = (): DOMException =>
  new DOMException("Hero vehicle loading was aborted", "AbortError");

const loadSceneWithAbort = async (
  dependencies: SuntraceHeroVehicleLoadDependencies,
  signal?: AbortSignal,
): Promise<THREE.Group> => {
  if (signal?.aborted) {
    throw createAbortError();
  }
  const loadPromise = dependencies.loadScene();
  if (signal === undefined) {
    return loadPromise;
  }
  return new Promise<THREE.Group>((resolve, reject) => {
    let settled = false;
    const onAbort = (): void => {
      if (settled) {
        return;
      }
      settled = true;
      dependencies.abort?.();
      void loadPromise.then(disposeVehicleRoot).catch(() => undefined);
      reject(createAbortError());
    };
    signal.addEventListener("abort", onAbort, { once: true });
    loadPromise.then(
      (scene) => {
        if (settled) {
          return;
        }
        settled = true;
        signal.removeEventListener("abort", onAbort);
        resolve(scene);
      },
      (error: unknown) => {
        if (settled) {
          return;
        }
        settled = true;
        signal.removeEventListener("abort", onAbort);
        reject(error instanceof Error ? error : new Error("Hero vehicle loading failed"));
      },
    );
  });
};

export const loadSuntraceHeroVehicleAsset = async (
  renderer: THREE.WebGLRenderer,
  signal?: AbortSignal,
  loadDependencies?: SuntraceHeroVehicleLoadDependencies,
): Promise<SuntraceAuthoredVehicleAsset> => {
  const dependencies = loadDependencies ?? createProductionLoader(renderer);
  let scene: THREE.Group | null = null;
  try {
    scene = await loadSceneWithAbort(dependencies, signal);
    if (signal?.aborted) {
      disposeVehicleRoot(scene);
      throw createAbortError();
    }
    const asset = createSuntraceAuthoredVehicleAsset(scene);
    scene = null;
    return asset;
  } catch (error) {
    if (scene !== null) {
      disposeVehicleRoot(scene);
    }
    if (signal?.aborted) {
      throw createAbortError();
    }
    throw error;
  } finally {
    dependencies.dispose?.();
  }
};

export const loadSuntraceHeroVehicleAssetOrFallback = async (
  renderer: THREE.WebGLRenderer,
  signal?: AbortSignal,
  loadDependencies?: SuntraceHeroVehicleLoadDependencies,
): Promise<SuntraceAuthoredVehicleAsset | null> => {
  try {
    return await loadSuntraceHeroVehicleAsset(
      renderer,
      signal,
      loadDependencies,
    );
  } catch (error) {
    if (
      signal?.aborted ||
      (error instanceof DOMException && error.name === "AbortError")
    ) {
      throw error;
    }
    return null;
  }
};
